'use client'

import { useState } from 'react'
import {
  TagIcon,
  PlusIcon,
  XIcon,
  MagnifyingGlassIcon,
} from '@phosphor-icons/react'
import { cn } from '@/lib/tailwind'
import { Input } from '@/ui/input'
import { ResponsiveSelector } from '@/ui/responsive-selector'
import { ICON_SIZES, FOCUS_STYLES } from '@/ui/design-tokens'

interface TopicSubtitleSelectorProps {
  selectedTopicIds: string[]
  availableTopics: Array<{ id: string; name: string }>
  onChange: (topicIds: string[]) => void
  onAddNew: () => void
  onCreateTopic?: (name: string) => void
  disabled?: boolean
}

export function TopicSubtitleSelector({
  selectedTopicIds,
  availableTopics,
  onChange,
  onAddNew,
  onCreateTopic,
  disabled = false,
}: TopicSubtitleSelectorProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [search, setSearch] = useState('')

  const selectedTopics = availableTopics.filter((t) =>
    selectedTopicIds.includes(t.id),
  )
  const firstTopic = selectedTopics[0]
  const extraCount = selectedTopics.length - 1

  const lowerSearch = search.toLowerCase()
  const filteredTopics = search
    ? availableTopics.filter((t) => t.name.toLowerCase().includes(lowerSearch))
    : availableTopics

  const toggleTopic = (id: string) => {
    if (selectedTopicIds.includes(id)) {
      onChange(selectedTopicIds.filter((s) => s !== id))
    } else {
      onChange([...selectedTopicIds, id])
    }
  }

  return (
    <ResponsiveSelector
      open={isOpen}
      onOpenChange={(open) => {
        setIsOpen(open)
        if (!open) setSearch('')
      }}
      ariaLabel="Select topics"
      contentClassName="sm:w-56"
      trigger={
        <button
          type="button"
          disabled={disabled}
          className={cn(
            'inline-flex items-center gap-1 rounded-sm px-1.5 py-0.5 text-sm transition-colors',
            'hover:bg-muted/30',
            selectedTopics.length > 0
              ? 'text-foreground'
              : 'text-muted-foreground hover:text-foreground',
            FOCUS_STYLES.ringCompact,
            disabled && 'pointer-events-none opacity-50',
          )}
        >
          <TagIcon
            className={cn(ICON_SIZES.compact, 'shrink-0')}
            weight="duotone"
          />
          {firstTopic ? (
            <>
              <span className="max-w-[120px] truncate sm:max-w-[140px]">
                {firstTopic.name}
              </span>
              {extraCount > 0 && (
                <span className="bg-foreground/10 shrink-0 rounded-sm px-1 text-xs">
                  +{extraCount}
                </span>
              )}
            </>
          ) : (
            <span>+ topic</span>
          )}
        </button>
      }
    >
      <div>
        <div className="relative mb-2">
          <MagnifyingGlassIcon className="text-muted-foreground pointer-events-none absolute top-1/2 left-2 h-3 w-3 -translate-y-1/2" />
          <Input
            type="text"
            placeholder="Search topics..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-8 pr-2 pl-7 text-sm"
          />
        </div>

        <div className="max-h-48 overflow-y-auto">
          <div className="space-y-0.5">
            {filteredTopics.length === 0 ? (
              search.trim() && onCreateTopic ? (
                <button
                  type="button"
                  onClick={() => {
                    onCreateTopic(search.trim())
                    setSearch('')
                    setIsOpen(false)
                  }}
                  className="text-muted-foreground hover:text-foreground hover:bg-muted flex min-h-[44px] w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm transition-colors sm:min-h-[32px]"
                >
                  <PlusIcon className={ICON_SIZES.compact} />
                  Add &ldquo;{search.trim()}&rdquo;
                </button>
              ) : (
                <p className="text-muted-foreground py-4 text-center text-sm">
                  No topics found
                </p>
              )
            ) : (
              filteredTopics.map((topic) => {
                const isSelected = selectedTopicIds.includes(topic.id)
                return (
                  <button
                    key={topic.id}
                    type="button"
                    onClick={() => toggleTopic(topic.id)}
                    className={cn(
                      'flex min-h-[44px] w-full items-center rounded-sm px-2 py-1.5 text-left text-sm transition-colors sm:min-h-[32px]',
                      isSelected
                        ? 'bg-foreground/10 text-foreground font-medium'
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted',
                    )}
                  >
                    {topic.name}
                  </button>
                )
              })
            )}
          </div>
        </div>

        {/* Fixed footer — always visible, outside scroll area */}
        {!search && (
          <>
            <div className="bg-border my-1 h-px" />
            <button
              type="button"
              onClick={() => {
                setIsOpen(false)
                onAddNew()
              }}
              className="text-muted-foreground hover:text-foreground hover:bg-muted flex min-h-[44px] w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm transition-colors sm:min-h-[32px]"
            >
              <PlusIcon className={ICON_SIZES.compact} />
              Add new topic...
            </button>
          </>
        )}

        {selectedTopicIds.length > 0 && (
          <>
            <div className="bg-border my-1 h-px" />
            <button
              type="button"
              onClick={() => onChange([])}
              className="text-muted-foreground hover:text-foreground hover:bg-muted flex min-h-[44px] w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm transition-colors sm:min-h-[32px]"
            >
              <XIcon className={ICON_SIZES.compact} />
              Clear all
            </button>
          </>
        )}
      </div>
    </ResponsiveSelector>
  )
}
